<script>
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { api, confirmar, sucesso } from '@/helpers/common/api';
import UsuarioModal from '@/components/crm/UsuarioModal.vue';

export default {
  name: 'UsuariosIndex',
  components: { Layout, PageHeader, UsuarioModal },
  data() {
    return {
      usuarios: [],
      menus: [],
      loading: false,
      modalAberto: false,
      usuarioEditando: null,
      currentUser: null,
      filtros: { busca: '' },
      paginacao: {
        paginaAtual: 1,
        registrosPorPagina: 25,
        totalRegistros: 0,
      },
      debounceTimer: null,
    };
  },
  computed: {
    totalPaginas() {
      return Math.ceil(this.paginacao.totalRegistros / this.paginacao.registrosPorPagina) || 1;
    },
  },
  async created() {
    this.currentUser = JSON.parse(localStorage.getItem('userdata')) || {};
    await Promise.all([this.carregarUsuarios(), this.carregarMenus()]);
  },
  methods: {
    async carregarUsuarios() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.filtros.busca) {
          params.set('search', this.filtros.busca);
          params.set('searchFields', 'nome,email');
        }
        const offset = (this.paginacao.paginaAtual - 1) * this.paginacao.registrosPorPagina;
        params.set('offset', String(offset));
        params.set('limit', String(this.paginacao.registrosPorPagina));
        params.set('order', JSON.stringify([['nome', 'asc']]));
        const qs = params.toString();
        const resultado = await api.read(`/usuario${qs ? `?${qs}` : ''}`);
        if (resultado && typeof resultado === 'object') {
          this.usuarios = Array.isArray(resultado) ? resultado : resultado.data || [];
          if (resultado.total !== undefined) {
            this.paginacao.totalRegistros = resultado.total;
          }
        }
      } finally {
        this.loading = false;
      }
    },

    async carregarMenus() {
      const result = await api.read('/menu?where=' + JSON.stringify({ status: 1 }) + '&include=' + JSON.stringify([{ model: 'Submenu', as: 'Submenu' }]));
      this.menus = Array.isArray(result) ? result : [];
    },

    debounceFiltros() {
      clearTimeout(this.debounceTimer);
      this.paginacao.paginaAtual = 1;
      this.debounceTimer = setTimeout(() => {
        this.carregarUsuarios();
      }, 800);
    },

    irParaPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginacao.paginaAtual = pagina;
        this.carregarUsuarios();
      }
    },

    mudarRegistrosPorPagina(novoLimite) {
      this.paginacao.registrosPorPagina = Number(novoLimite);
      this.paginacao.paginaAtual = 1;
      this.carregarUsuarios();
    },

    abrirModal(usuario = null) {
      this.usuarioEditando = usuario;
      this.modalAberto = true;
    },

    fecharModal() {
      this.modalAberto = false;
      this.usuarioEditando = null;
    },

    onUsuarioSalvo() {
      this.fecharModal();
      this.carregarUsuarios();
    },

    excluirUsuario(usuario) {
      if (usuario.id === this.currentUser.id) return;

      confirmar({
        options: {
          title: 'Excluir usuário?',
          text: `Deseja realmente excluir "${usuario.nome}"? Esta ação não pode ser revertida.`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, excluir',
          customClass: {
            confirmButton: 'btn btn-danger w-xs me-2',
            cancelButton: 'btn btn-secondary w-xs',
          },
          buttonsStyling: false,
        },
        confirmado: async () => {
          const result = await api.destroy(`/usuario/${usuario.id}`);
          if (result) {
            sucesso({ title: 'Usuário excluído com sucesso!' });
            this.carregarUsuarios();
          }
        },
      });
    },

    formatarMenus(usuario) {
      if (usuario.super === 1) return 'Todos (admin)';
      if (!Array.isArray(usuario.menus) || !usuario.menus.length) return 'Nenhum';
      const nomes = usuario.menus
        .map((id) => {
          const menu = this.menus.find((m) => m.id === id);
          return menu ? menu.name : null;
        })
        .filter(Boolean);
      return nomes.length ? nomes.join(', ') : 'Nenhum';
    },

    formatarData(data) {
      if (!data) return '—';
      return new Date(data).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Usuários" pageTitle="Cadastros" />

    <BRow>
      <BCol lg="12">
        <BCard no-body>
          <div class="card-header border-0 d-flex align-items-center">
            <h5 class="card-title mb-0 flex-grow-1">Lista de Usuários</h5>
          </div>
          <div class="card-body">
            <BRow class="g-3 mb-3 align-items-center">
              <BCol sm="4">
                <div class="search-box">
                  <input
                    v-model="filtros.busca"
                    type="text"
                    class="form-control search"
                    placeholder="Buscar por nome ou email..."
                    @input="debounceFiltros"
                  />
                  <i class="ri-search-line search-icon"></i>
                </div>
              </BCol>
              <BCol sm="2">
                <select
                  v-model.number="paginacao.registrosPorPagina"
                  class="form-select"
                  @change="mudarRegistrosPorPagina(paginacao.registrosPorPagina)"
                >
                  <option :value="25">25 por página</option>
                  <option :value="50">50 por página</option>
                  <option :value="100">100 por página</option>
                </select>
              </BCol>
              <BCol sm="6" class="text-end">
                <BButton variant="soft-success" @click="abrirModal()">
                  <i class="ri-add-line align-bottom me-1"></i> Novo Usuário
                </BButton>
              </BCol>
            </BRow>

            <div v-if="loading" class="text-center py-4">
              <BSpinner variant="primary" />
            </div>
            <div v-else class="table-responsive">
              <table class="table table-borderless align-middle table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Perfil</th>
                    <th>Menus</th>
                    <th>Status</th>
                    <th>Último login</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="usuario in usuarios" :key="usuario.id">
                    <td class="fw-medium">{{ usuario.nome }}</td>
                    <td>{{ usuario.email }}</td>
                    <td>
                      <span
                        :class="usuario.super === 1
                          ? 'badge bg-primary-subtle text-primary'
                          : 'badge bg-info-subtle text-info'"
                      >
                        {{ usuario.super === 1 ? 'Administrador' : 'Usuário' }}
                      </span>
                    </td>
                    <td>
                      <span class="text-muted" style="max-width: 200px; display: inline-block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                        {{ formatarMenus(usuario) }}
                      </span>
                    </td>
                    <td>
                      <span
                        :class="usuario.ativo
                          ? 'badge bg-success-subtle text-success'
                          : 'badge bg-danger-subtle text-danger'"
                      >
                        {{ usuario.ativo ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td class="text-muted">{{ formatarData(usuario.ultimo_login_em) }}</td>
                    <td>
                      <div class="d-flex gap-1">
                        <BButton variant="soft-info" size="sm" @click="abrirModal(usuario)" title="Editar">
                          <i class="ri-edit-line"></i>
                        </BButton>
                        <BButton
                          v-if="usuario.id !== currentUser.id"
                          variant="soft-danger"
                          size="sm"
                          @click="excluirUsuario(usuario)"
                          title="Excluir"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </BButton>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="!usuarios.length">
                    <td colspan="7" class="text-center text-muted py-4">Nenhum usuário encontrado.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <BRow class="mt-3 align-items-center">
              <BCol md="6" class="d-flex align-items-center">
                <small class="text-muted">
                  Mostrando
                  <strong>{{ paginacao.totalRegistros ? (paginacao.paginaAtual - 1) * paginacao.registrosPorPagina + 1 : 0 }}</strong>
                  a
                  <strong>{{ Math.min(paginacao.paginaAtual * paginacao.registrosPorPagina, paginacao.totalRegistros) }}</strong>
                  de
                  <strong>{{ paginacao.totalRegistros }}</strong>
                  registros
                </small>
              </BCol>
              <BCol md="6" class="text-end">
                <nav aria-label="Paginação">
                  <BPagination
                    v-model="paginacao.paginaAtual"
                    :total-rows="paginacao.totalRegistros"
                    :per-page="paginacao.registrosPorPagina"
                    :limit="5"
                    align="end"
                    size="sm"
                    @update:modelValue="irParaPagina"
                  />
                </nav>
              </BCol>
            </BRow>
          </div>
        </BCard>
      </BCol>
    </BRow>

    <UsuarioModal
      :visivel="modalAberto"
      :usuario="usuarioEditando"
      :menus="menus"
      :current-user-id="currentUser.id"
      @fechar="fecharModal"
      @salvo="onUsuarioSalvo"
    />
  </Layout>
</template>
